// The six run stages, and the map from the wire constant to what the trader reads.
//
// Ruling L33: a stage constant is an IDENTIFIER the panel maps to its own label. A raw stage value
// is NEVER interpolated into the view. Rename a stage on the backend and this map fails to resolve
// — a hole you can see — instead of putting a camelCase identifier on the trader's screen.
//
// Ruling L28: the rail shows stage NAMES and no number of any kind. Not a day count, not a card
// count, not a percentage. A figure on the rail is something the trader can subtract the visible
// cards from, which is exactly the reconciliation R-C removes. MavenRunStage.detail is therefore
// not rendered by this lane.
//
// Ruling L33 again, on the first stage: this is `candidateDays`, not `daysFound`. The old coinage
// primed a count; "candidate days" is the feature request's own term (§3.4) and matches contract
// M1 §4.1's "candidate dates".

export const MAVEN_STAGE_CANDIDATE_DAYS = 'candidateDays';
export const MAVEN_STAGE_ROLL = 'roll';
export const MAVEN_STAGE_HEDGES = 'hedges';
export const MAVEN_STAGE_CLOSE = 'close';
export const MAVEN_STAGE_PRICING = 'pricing';
export const MAVEN_STAGE_RANKING = 'ranking';

/**
 * The stages in the order the backend reaches them. The rail renders this order, and lights each
 * entry as its event arrives.
 *
 * `close` is reached only when the trader chose Close for the existing hedges; on Ignore nothing is
 * ordered from the Close engine, so no such event arrives and that entry never lights.
 */
export const MAVEN_STAGE_ORDER: string[] = [
    MAVEN_STAGE_CANDIDATE_DAYS,
    MAVEN_STAGE_ROLL,
    MAVEN_STAGE_HEDGES,
    MAVEN_STAGE_CLOSE,
    MAVEN_STAGE_PRICING,
    MAVEN_STAGE_RANKING,
];

// Descriptive labels (owner, 2026-09-13): each names what the run is DOING, not the supplier it
// ordered from. "Close" alone read as a weird stage — and appeared even when nothing was closed.
const STAGE_LABELS: { [stage: string]: string } = {
    [MAVEN_STAGE_CANDIDATE_DAYS]: 'Finding candidate roll days',
    [MAVEN_STAGE_ROLL]: 'Building the adjustment per day',
    [MAVEN_STAGE_HEDGES]: 'Building hedges per day',
    [MAVEN_STAGE_CLOSE]: 'Closing the existing hedges',
    [MAVEN_STAGE_PRICING]: 'Pricing all packages',
    [MAVEN_STAGE_RANKING]: 'Ranking by credit per day',
};

/**
 * The three supplier stages — roll, hedges, close — are raised back to back at the START of one
 * parallel phase, so the last of them would otherwise sit on screen while all three work. The
 * spinner names the phase as one message instead (owner, 2026-09-13). "and close" appears only
 * when the close stage was actually raised, i.e. the trader chose Close for the existing hedges.
 */
export function mavenSupplierPhaseLabel(closing: boolean): string {
    return closing
        ? 'Building adjustment, hedges and close per day'
        : 'Building adjustment and hedges per day';
}

/** Whether a stage constant belongs to the merged supplier phase. */
export function isMavenSupplierStage(stage: string): boolean {
    return stage === MAVEN_STAGE_ROLL || stage === MAVEN_STAGE_HEDGES || stage === MAVEN_STAGE_CLOSE;
}

/**
 * The label for a stage constant, or null when the constant is not one of the six.
 *
 * Null rather than the constant itself, deliberately: falling back to the raw value is how an
 * identifier reaches the screen. A caller that gets null renders nothing for that stage — an
 * unknown code is a defect (guarantee G12), not something to display.
 */
export function mavenStageLabel(stage: string): string | null {
    if (!stage) {
        return null;
    }

    const label = STAGE_LABELS[stage];

    return label === undefined ? null : label;
}

/** Whether a stage constant is one of the six the contract defines. */
export function isKnownMavenStage(stage: string): boolean {
    return mavenStageLabel(stage) !== null;
}
